/**
 * Secret sewer system — glowing tunnel network under the city connecting the
 * manholes (fast travel), with a hidden graffiti lounge for those who explore.
 * Own coordinate space (SEWER_W x SEWER_H); camera follows both axes.
 */

export const SEWER_W = 1600;
export const SEWER_H = 1260;

export const SEWER_RECTS = [
  { x1: 80, y1: 520, x2: 1520, y2: 640 },    // main east-west tunnel
  { x1: 120, y1: 130, x2: 240, y2: 640 },    // west shaft (Club St ladder)
  { x1: 400, y1: 520, x2: 520, y2: 1130 },   // south shaft (Palm Court ladder)
  { x1: 720, y1: 130, x2: 840, y2: 640 },    // north shaft (Ironworks ladder)
  { x1: 1360, y1: 130, x2: 1480, y2: 640 },  // east shaft (Fairgrounds ladder)
  { x1: 1140, y1: 640, x2: 1216, y2: 940 },  // narrow secret branch...
  { x1: 980, y1: 940, x2: 1460, y2: 1200 },  // ...into the graffiti lounge
];

// ladder ↔ manhole mapping (spawn just below the ladder)
export const LADDERS = [
  { manhole: 'club', x: 180, y: 175, label: 'CLUB ST' },
  { manhole: 'palm', x: 460, y: 1085, label: 'PALM CT' },
  { manhole: 'iron', x: 780, y: 175, label: 'IRONWORKS' },
  { manhole: 'fair', x: 1420, y: 175, label: 'FAIRGROUNDS' },
];

const inRect = (x, y, r) => x >= r.x1 && x <= r.x2 && y >= r.y1 && y <= r.y2;

export function drawSewer(ctx, camX, camY, W, H, ph) {
  const t = ph * 0.05;
  // solid rock everywhere
  ctx.fillStyle = '#0a0d0c';
  ctx.fillRect(0, 0, W, H);
  ctx.save();
  ctx.translate(-camX, -camY);
  // tunnels: floor + wall lips
  SEWER_RECTS.forEach((r, ri) => {
    const isLounge = ri === 6;
    ctx.fillStyle = isLounge ? '#241b30' : '#1b2420';
    ctx.fillRect(r.x1, r.y1, r.x2 - r.x1, r.y2 - r.y1);
    ctx.strokeStyle = isLounge ? '#4c3a63' : '#2f3d36';
    ctx.lineWidth = 6;
    ctx.strokeRect(r.x1, r.y1, r.x2 - r.x1, r.y2 - r.y1);
    // brick texture
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 1;
    for (let by = r.y1 + 18; by < r.y2; by += 26) {
      ctx.beginPath(); ctx.moveTo(r.x1, by); ctx.lineTo(r.x2, by); ctx.stroke();
    }
  });
  // glowing water channel along the main tunnel
  const flow = (ph * 1.2) % 64;
  ctx.fillStyle = 'rgba(52,211,153,0.28)';
  ctx.fillRect(96, 566, 1408, 28);
  ctx.fillStyle = 'rgba(52,211,153,0.5)';
  for (let wx = 96 - 64 + flow; wx < 1504; wx += 64) {
    ctx.beginPath();
    ctx.ellipse(Math.max(110, Math.min(1490, wx)), 580, 18, 5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.save();
  ctx.shadowColor = '#34d399';
  ctx.shadowBlur = 18;
  ctx.strokeStyle = 'rgba(52,211,153,0.65)';
  ctx.lineWidth = 2;
  ctx.strokeRect(96, 566, 1408, 28);
  ctx.restore();
  // vertical shaft drips
  [[180, 130], [460, 1130], [780, 130], [1420, 130]].forEach(([dx], i) => {
    const dripY = ((ph * (1.6 + i * 0.3) + i * 120) % 400);
    ctx.fillStyle = 'rgba(103,232,249,0.65)';
    ctx.fillRect(dx + 24, 140 + dripY, 2, 7);
  });
  // wall pipes along the main tunnel
  ctx.strokeStyle = '#3d4b44';
  ctx.lineWidth = 8;
  ctx.beginPath(); ctx.moveTo(90, 540); ctx.lineTo(1510, 540); ctx.stroke();
  ctx.strokeStyle = '#2c3831';
  ctx.lineWidth = 3;
  for (let px = 160; px < 1500; px += 130) {
    ctx.beginPath(); ctx.arc(px, 540, 8, 0, Math.PI * 2); ctx.stroke();
  }
  // lamps
  [[300, 528], [640, 528], [1000, 528], [1300, 528], [460, 900], [780, 300], [1420, 300], [180, 300]].forEach(([lx, ly], i) => {
    const gl = 0.5 + Math.abs(Math.sin(t * 1.4 + i * 2)) * 0.5;
    ctx.fillStyle = `rgba(251,191,36,${gl})`;
    ctx.beginPath(); ctx.arc(lx, ly, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = `rgba(251,191,36,${gl * 0.08})`;
    ctx.beginPath(); ctx.arc(lx, ly + 8, 44, 0, Math.PI * 2); ctx.fill();
  });
  // rats scurrying the main tunnel
  for (let i = 0; i < 2; i++) {
    const span = 1300;
    const tt = (ph * (1.5 + i) + i * 700) % (span * 2);
    const rx = 120 + (tt < span ? tt : span * 2 - tt);
    const ry = 616 + i * 10;
    ctx.fillStyle = '#3f3a45';
    ctx.beginPath(); ctx.ellipse(rx, ry, 7, 3.5, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(rx + (tt < span ? 7 : -7), ry - 1, 2.5, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#3f3a45'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(rx - (tt < span ? 7 : -7), ry); ctx.quadraticCurveTo(rx - (tt < span ? 13 : -13), ry - 3, rx - (tt < span ? 16 : -16), ry + 1); ctx.stroke();
  }
  // ladders
  LADDERS.forEach((l) => {
    ctx.strokeStyle = '#8a8f9c';
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(l.x - 10, l.y - 44); ctx.lineTo(l.x - 10, l.y + 14); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(l.x + 10, l.y - 44); ctx.lineTo(l.x + 10, l.y + 14); ctx.stroke();
    for (let s = 0; s < 6; s++) {
      ctx.beginPath(); ctx.moveTo(l.x - 10, l.y - 38 + s * 10); ctx.lineTo(l.x + 10, l.y - 38 + s * 10); ctx.stroke();
    }
    // light shaft from above
    ctx.fillStyle = 'rgba(253,230,138,0.07)';
    ctx.beginPath();
    ctx.moveTo(l.x - 12, l.y - 46); ctx.lineTo(l.x + 12, l.y - 46);
    ctx.lineTo(l.x + 26, l.y + 20); ctx.lineTo(l.x - 26, l.y + 20);
    ctx.closePath(); ctx.fill();
    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillText(`⬆ ${l.label}`, l.x, l.y + 30);
  });
  // ===== GRAFFITI LOUNGE =====
  const L = SEWER_RECTS[6];
  // string lights
  for (let i = 0; i < 10; i++) {
    const on = (Math.floor(ph / 12) + i) % 3 === 0;
    ctx.fillStyle = on ? '#f472b6' : 'rgba(244,114,182,0.3)';
    ctx.beginPath(); ctx.arc(L.x1 + 30 + i * 42, L.y1 + 22 + Math.sin(i * 1.3) * 5, 3, 0, Math.PI * 2); ctx.fill();
  }
  // graffiti tags
  ctx.save();
  ctx.font = 'bold 26px monospace';
  ctx.textAlign = 'center';
  ctx.shadowBlur = 12;
  ctx.shadowColor = '#22d3ee';
  ctx.fillStyle = '#22d3ee';
  ctx.save(); ctx.translate(L.x1 + 105, L.y1 + 78); ctx.rotate(-0.12); ctx.fillText('RZNT', 0, 0); ctx.restore();
  ctx.shadowColor = '#f472b6';
  ctx.fillStyle = '#f472b6';
  ctx.font = 'bold 20px monospace';
  ctx.save(); ctx.translate(L.x1 + 265, L.y1 + 60); ctx.rotate(0.08); ctx.fillText('TURTLE GANG 🐢', 0, 0); ctx.restore();
  ctx.shadowColor = '#fde047';
  ctx.fillStyle = '#fde047';
  ctx.font = 'bold 15px monospace';
  ctx.save(); ctx.translate(L.x1 + 400, L.y1 + 95); ctx.rotate(-0.06); ctx.fillText('you found us', 0, 0); ctx.restore();
  ctx.restore();
  // neon sign
  const gl2 = 0.6 + Math.abs(Math.sin(t * 2)) * 0.4;
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = `rgba(52,211,153,${gl2})`;
  ctx.shadowColor = '#34d399';
  ctx.shadowBlur = 16 * gl2;
  ctx.fillText('⚡ THE UNDERGROUND ⚡', (L.x1 + L.x2) / 2, L.y1 + 132);
  ctx.shadowBlur = 0;
  // couch
  const cy2 = L.y2 - 90;
  ctx.fillStyle = '#7c2d6e';
  ctx.fillRect(L.x1 + 60, cy2, 130, 40);
  ctx.fillRect(L.x1 + 52, cy2 - 22, 12, 62);
  ctx.fillRect(L.x1 + 186, cy2 - 22, 12, 62);
  ctx.fillStyle = '#9d3b8b';
  ctx.fillRect(L.x1 + 64, cy2 - 18, 122, 22);
  // boombox with pulsing speakers
  const bx = L.x2 - 160;
  ctx.fillStyle = '#1c1c24';
  ctx.fillRect(bx, cy2 + 4, 84, 40);
  const pump = 1 + Math.abs(Math.sin(t * 4)) * 0.25;
  ctx.fillStyle = '#38bdf8';
  ctx.beginPath(); ctx.arc(bx + 20, cy2 + 24, 11 * pump, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(bx + 64, cy2 + 24, 11 * pump, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#0b0d12';
  ctx.beginPath(); ctx.arc(bx + 20, cy2 + 24, 5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(bx + 64, cy2 + 24, 5, 0, Math.PI * 2); ctx.fill();
  // music notes
  for (let i = 0; i < 3; i++) {
    const cyc = (ph * 0.8 + i * 33) % 90;
    ctx.globalAlpha = Math.max(0, 0.8 - cyc / 90);
    ctx.font = '13px sans-serif';
    ctx.fillStyle = '#67e8f9';
    ctx.fillText(i % 2 ? '🎵' : '🎶', bx + 42 + Math.sin(cyc * 0.1 + i) * 16, cy2 - cyc * 0.5);
    ctx.globalAlpha = 1;
  }
  // beanbags
  ctx.fillStyle = '#166534';
  ctx.beginPath(); ctx.ellipse(L.x1 + 300, cy2 + 26, 26, 16, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#1d4ed8';
  ctx.beginPath(); ctx.ellipse(L.x1 + 370, cy2 + 30, 26, 16, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

export const sewerInside = (x, y) => SEWER_RECTS.some((r) => inRect(x, y, r));
